import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FileRejection, useDropzone } from 'react-dropzone';

import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';
import FileDetails, { FileDetailsModel } from './file-details/FileDetails';
import Button from '../button/Button';
import Icon from '../../icon/Icon';
import { UploadConfig } from '../../../core/services/ProjectDocumentsService';
import ConfirmModal from '../../../pages/components/confirm-modal/ConfirmModal';

import './Upload.scss';

interface Props {
  accept?: string | string[];
  options?: {
    maxFiles?: number;
    maxSize?: number;
  };
  messages?: {
    fileTooLarge?: string;
    fileInvalidType?: string;
    tooManyFiles?: string;
  };
  confirmModal?: {
    titleText: string;
    contentText: string;
  };
  removeModal?: {
    titleText: string;
    contentText: string;
  };
  onDrop?: (formData: FormData, config: UploadConfig) => void | Promise<void>;
  onRemove?: (fileDetails: FileDetailsModel) => void;
  fileDetails?: FileDetailsModel[];
  disabled?: boolean;
  disabledMessage?: string;
}

const Upload: React.FC<Props> = ({
  accept,
  options,
  messages,
  onDrop,
  onRemove,
  fileDetails,
  confirmModal,
  removeModal,
  disabled = false,
  disabledMessage
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadingFilename, setUploadingFilename] = useState<string>('');
  const [isConfirmVisible, setConfirmVisible] = useState(false);
  const [currentFiles, setCurrentFiles] = useState<File[]>();

  const onDropAccepted = useCallback(
    async (files: File[]) => {
      setUploadInProgress(true);
      setUploadingFilename(files.map((file) => file.name).join(', '));
      setUploadProgress(0);

      const formData = new FormData();
      files.forEach((file) => formData.append('files', file, file.name));

      const config = {
        onUploadProgress: (progressEvent: ProgressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      };

      if (onDrop) {
        await onDrop(formData, config);
      }

      setUploadInProgress(false);
    },
    [onDrop]
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errorCode = rejections[0].errors[0].code;
      let message;
      switch (errorCode) {
        case 'file-too-large':
          message = messages?.fileTooLarge ?? t('file.sizeValidation.default');
          break;
        case 'file-invalid-type':
          message = messages?.fileInvalidType ?? t('file.typeValidation.default');
          break;
        case 'too-many-files':
          message = messages?.tooManyFiles ?? t('file.countValidation.default');
          break;
        default:
          message = t('file.unknownError.default');
      }

      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Error,
          message,
          delay: 5000
        })
      );
    },
    [dispatch, t, messages]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    disabled,
    maxFiles: options?.maxFiles ?? 1,
    maxSize: options?.maxSize,
    onDrop: () => {},
    onDropAccepted: (files: File[]) => {
      if (fileDetails?.length && confirmModal) {
        setConfirmVisible(true);
        setCurrentFiles(files);
      } else {
        onDropAccepted(files);
      }
    },
    onDropRejected
  });

  return (
    <section className="upload">
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div className="upload__drop-area" {...getRootProps()}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...getInputProps()} />
        <Icon icon="upload" />
        {disabled ? (
          <div className="upload__text upload__text--disabled">{disabledMessage}</div>
        ) : (
          <>
            <div className="upload__text">{t('file.dragDrop')}</div>
            <Button buttonTypes={['blue-outline', 'xx-small']} className="upload__button">
              {t('file.chooseFile')}
            </Button>
          </>
        )}
      </div>
      <div className="upload__file-details">
        {uploadInProgress && (
          <FileDetails uploadingFilename={uploadingFilename} uploadProgress={uploadProgress} uploadInProgress removeModal={removeModal} />
        )}
        {fileDetails?.map((details) => (
          <FileDetails
            key={details.id}
            uploadingFilename=""
            uploadProgress={0}
            uploadInProgress={false}
            fileDetails={details}
            removeModal={removeModal}
            onRemove={onRemove ? () => onRemove(details) : undefined}
          />
        ))}
      </div>
      <ConfirmModal
        titleText={confirmModal?.titleText}
        contentText={confirmModal?.contentText}
        isOpen={isConfirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onConfirm={() => {
          if (currentFiles) {
            onDropAccepted(currentFiles);
          }
          setConfirmVisible(false);
        }}
      />
    </section>
  );
};

export default Upload;
